/**
 * STABLE identity by `id` for live-query rows (F4-D).
 *
 * Live-queries (PowerSync/TanStack) return a NEW array of NEW objects on
 * every tick even when the values don't change. Solid's `<For>` keys
 * by REFERENCE, so every sync echo REMOUNTS every row — and with
 * them dies any internal interactive state (open dialogs, focus,
 * uploads in progress). Real symptom: authoring's "insert video" dialog
 * closed itself ~200ms after opening it (write-path echo).
 *
 * This memo keeps each row's previous reference when its values
 * are shallow-equal: `<For>` only remounts rows whose content changed.
 */
import { createMemo } from "solid-js";

/** STRUCTURAL equality for JSON columns (the echo re-serializes the server's
 * jsonb with a different key order/spacing — the string differs, the value doesn't). */
function jsonEqual(a: unknown, b: unknown): boolean {
  if (Object.is(a, b)) {
    return true;
  }
  if (typeof a !== "string" || typeof b !== "string") {
    return false;
  }
  try {
    return canonical(JSON.parse(a)) === canonical(JSON.parse(b));
  } catch {
    return false;
  }
}

function canonical(value: unknown): string {
  if (Array.isArray(value)) {
    return `[${value.map(canonical).join(",")}]`;
  }
  if (value !== null && typeof value === "object") {
    const entries = Object.entries(value)
      .toSorted(([a], [b]) => a.localeCompare(b))
      .map(([k, v]) => `${JSON.stringify(k)}:${canonical(v)}`);
    return `{${entries.join(",")}}`;
  }
  return JSON.stringify(value);
}

function shallowEqual(
  a: Record<string, unknown>,
  b: Record<string, unknown>,
  ignore: ReadonlySet<string>,
  jsonFields: ReadonlySet<string>,
): boolean {
  const ka = Object.keys(a);
  const kb = Object.keys(b);
  if (ka.length !== kb.length) {
    return false;
  }
  for (const k of ka) {
    if (ignore.has(k)) {
      continue;
    }
    if (jsonFields.has(k)) {
      if (!jsonEqual(a[k], b[k])) {
        return false;
      }
      continue;
    }
    if (!Object.is(a[k], b[k])) {
      return false;
    }
  }
  return true;
}

export interface StableRowsOptions {
  /**
   * VOLATILE columns that don't count as a real change (e.g. `updated_at`,
   * which the server-authoritative write-path re-stamps with `now()` on every echo).
   * If a row only differs on these columns, the previous reference is
   * kept (those fields stay "frozen" for the UI — document it if your
   * component shows them).
   */
  ignore?: readonly string[];
  /**
   * JSON text columns (jsonb) compared by STRUCTURE, not by
   * string: the server's echo re-serializes the jsonb (key order,
   * spacing, COALESCE defaults) and the string differs even when the value doesn't.
   */
  jsonFields?: readonly string[];
}

export function stableRows<T extends { id: string }>(
  rows: () => readonly T[] | undefined,
  options?: StableRowsOptions,
): () => T[] {
  const ignore = new Set(options?.ignore ?? []);
  const jsonFields = new Set(options?.jsonFields ?? []);
  let cache = new Map<string, T>();
  const stable = createMemo(() => {
    const next = rows() ?? [];
    const out: T[] = [];
    const nextCache = new Map<string, T>();
    for (const row of next) {
      const prev = cache.get(row.id);
      const kept =
        prev &&
        shallowEqual(
          prev as Record<string, unknown>,
          row as Record<string, unknown>,
          ignore,
          jsonFields,
        )
          ? prev
          : row;
      out.push(kept);
      nextCache.set(row.id, kept);
    }
    cache = nextCache;
    return out;
  });
  return stable;
}
